/**
 * Trend Tracker fixture — seeds the Trends page until the real Supabase-backed
 * /v2/trends/* endpoints land. Content is derived from the hardcoded mock that
 * lived inside TrendsPage.tsx, promoted to the full Supabase-shaped Trend model
 * (signals, snapshots, relevance) so every UI widget has something to render.
 */

import type { Trend } from '@/utils/trend';

function buildSnapshots(peak: number, days = 21, shape: 'rising' | 'peaking' | 'declining' = 'rising'): Trend['snapshots'] {
  const out: Trend['snapshots'] = [];
  const base = new Date('2026-04-01T00:00:00Z');
  for (let i = 0; i < days; i += 1) {
    const d = new Date(base);
    d.setUTCDate(base.getUTCDate() + i);
    const progress = i / (days - 1);
    let score: number;
    if (shape === 'rising') {
      score = Math.round(peak * 0.2 + peak * 0.8 * progress);
    } else if (shape === 'peaking') {
      score = Math.round(peak - peak * 0.15 * Math.abs(progress - 0.5) * 2);
    } else {
      score = Math.round(peak - peak * 0.6 * progress);
    }
    out.push({
      snapshot_date: d.toISOString().slice(0, 10),
      virality_score: score,
      mention_count: Math.round(50 + 200 * progress),
      search_volume: Math.round(1000 + 5000 * progress),
    });
  }
  return out;
}

const TRENDS: Trend[] = [
  {
    id: 's1',
    slug: 'de-influencing-movement',
    title: 'De-influencing Movement',
    description:
      'Creators openly sharing what NOT to buy, calling out overhyped products to build extreme trust with audiences.',
    why_it_matters:
      'Shifts power from brands to creators. Brands must now prove efficacy rather than just buying reach.',
    category: 'social',
    origin_platform: 'tiktok',
    virality_score: 94,
    status: 'peaking',
    geo_scope: 'global',
    first_seen_at: '2026-03-10T00:00:00Z',
    last_updated: '2026-04-20T00:00:00Z',
    metadata: {
      hashtags: ['#deinfluencing', '#honestreview', '#donotbuy'],
      related_keywords: ['de-influencing', 'product dupe', 'honest review'],
      cover_image_url: 'https://images.unsplash.com/photo-1697125045982-0d703bc35e96?w=800&q=80',
    },
    signals: [
      { id: 'sig-1', source: 'beatly_mentions', signal_type: 'keyword_velocity', raw_data: { keyword: 'deinfluencing', velocity_ratio: 3.2 }, geo: 'US', recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-2', source: 'tiktok_cc', signal_type: 'hashtag_spike', raw_data: { hashtag: '#deinfluencing' }, geo: 'global', recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-3', source: 'google_trends', signal_type: 'search_volume', raw_data: { keyword: 'de-influencing', velocity_ratio: 2.1 }, geo: 'SE', recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-4', source: 'reddit', signal_type: 'viral_post', raw_data: { sample_post_url: 'https://reddit.com/r/BeautyGuruChatter/comments/xyz' }, recorded_at: '2026-04-17T00:00:00Z' },
    ],
    snapshots: buildSnapshots(94, 21, 'peaking'),
    relevance: {
      relevance_score: 88,
      reasoning:
        'High match to your active H&M Spring campaign — your audience skews sceptical-Gen-Z, exactly the cohort driving this trend.',
      suggested_action:
        'Brief micro-creators on a "3 products we DO love" spin — flips de-influencing into a trust-win for your brand.',
      dismissed: false,
    },
  },
  {
    id: 's2',
    slug: 'quiet-luxury-aesthetic',
    title: 'Quiet Luxury Aesthetic',
    description:
      'Understated wealth signals. No logos, timeless quality over flashy branding. A reaction to post-pandemic excess.',
    why_it_matters:
      'Requires a shift in visual storytelling from flashy and loud to high-quality, craft-focused narratives.',
    category: 'social',
    origin_platform: 'instagram',
    virality_score: 87,
    status: 'active',
    geo_scope: 'EU',
    first_seen_at: '2026-02-01T00:00:00Z',
    last_updated: '2026-04-20T00:00:00Z',
    metadata: {
      hashtags: ['#quietluxury', '#stealthwealth'],
      related_keywords: ['old money', 'stealth wealth', 'minimalist luxury'],
      cover_image_url: 'https://images.unsplash.com/photo-1758297679736-2e6ff92d2021?w=800&q=80',
    },
    signals: [
      { id: 'sig-5', source: 'google_trends', signal_type: 'search_volume', raw_data: { keyword: 'quiet luxury', velocity_ratio: 1.4 }, geo: 'EU', recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-6', source: 'beatly_mentions', signal_type: 'keyword_velocity', raw_data: { keyword: 'stealth wealth', velocity_ratio: 1.8 }, recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-7', source: 'newsapi', signal_type: 'viral_post', raw_data: { keyword: 'quiet luxury trend' }, recorded_at: '2026-04-17T00:00:00Z' },
    ],
    snapshots: buildSnapshots(87, 21, 'active' as 'peaking'),
    relevance: {
      relevance_score: 76,
      reasoning:
        'Matches the tonality of Bixia Klimathjälte — premium, low-volume creative. Slight over-index on UK/FR vs your SE focus.',
      suggested_action:
        'Consider a craft-focused video with macro creators; pair with a single product (not a catalog).',
      dismissed: false,
    },
  },
  {
    id: 's3',
    slug: 'ai-ugc-revolution',
    title: 'AI UGC Revolution',
    description:
      'AI-generated user content, synthetic creators, and AI-enhanced testimonials gaining mainstream traction.',
    why_it_matters:
      'Massively lowers production costs and allows for hyper-personalized creative testing at scale.',
    category: 'content',
    origin_platform: 'cross-platform',
    virality_score: 78,
    status: 'emerging',
    geo_scope: 'global',
    first_seen_at: '2026-03-25T00:00:00Z',
    last_updated: '2026-04-20T00:00:00Z',
    metadata: {
      hashtags: ['#aiugc', '#syntheticcreator'],
      related_keywords: ['ai ugc', 'synthetic influencer', 'virtual creator'],
      cover_image_url: 'https://images.unsplash.com/photo-1770233621425-5d9ee7a0a700?w=800&q=80',
    },
    signals: [
      { id: 'sig-8', source: 'reddit', signal_type: 'viral_post', raw_data: { hashtag: 'aiugc' }, recorded_at: '2026-04-15T00:00:00Z' },
      { id: 'sig-9', source: 'tiktok_cc', signal_type: 'hashtag_spike', raw_data: { hashtag: '#aiugc' }, recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-10', source: 'google_trends', signal_type: 'search_volume', raw_data: { keyword: 'ai ugc', velocity_ratio: 2.6 }, recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-11', source: 'ad_anomaly', signal_type: 'engagement_anomaly', raw_data: { keyword: 'ai-generated ad', z_score: 2.4 }, recorded_at: '2026-04-19T00:00:00Z' },
    ],
    snapshots: buildSnapshots(78, 21, 'rising'),
    relevance: {
      relevance_score: 85,
      reasoning:
        'Directly lowers your creator-production cost base. Your past campaigns show a tolerance for format experimentation.',
      suggested_action:
        'Run a paid A/B: AI-generated creator variant vs. human creator variant on the same brief, 10% budget split.',
      dismissed: false,
    },
  },
  {
    id: 'c1',
    slug: 'micro-expert-archetype',
    title: 'Micro-Expert Archetype',
    description:
      'Highly specialized creators (e.g., cosmetic chemists, tax lawyers) replacing general influencers.',
    why_it_matters:
      'Higher conversion rates due to perceived authority. Niche reach is more valuable than mass reach.',
    category: 'creator',
    origin_platform: 'tiktok',
    virality_score: 85,
    status: 'active',
    geo_scope: 'global',
    first_seen_at: '2026-03-01T00:00:00Z',
    last_updated: '2026-04-20T00:00:00Z',
    metadata: {
      hashtags: ['#microexpert', '#nichecreator'],
      related_keywords: ['expert creator', 'niche influencer', 'specialist'],
      cover_image_url: 'https://images.unsplash.com/photo-1768295984707-64ab37e539dd?w=800&q=80',
    },
    signals: [
      { id: 'sig-12', source: 'beatly_mentions', signal_type: 'keyword_velocity', raw_data: { keyword: 'micro expert', velocity_ratio: 1.9 }, recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-13', source: 'tiktok_cc', signal_type: 'hashtag_spike', raw_data: { hashtag: '#nichecreator' }, recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-14', source: 'youtube', signal_type: 'viral_post', raw_data: { keyword: 'niche expert creator' }, recorded_at: '2026-04-17T00:00:00Z' },
    ],
    snapshots: buildSnapshots(85, 21, 'peaking'),
    relevance: {
      relevance_score: 89,
      reasoning:
        'Strong fit for your sustainability-leaning portfolio (Bixia, Resurs Bank). Specialist voice matches your tone.',
      suggested_action:
        'Shortlist 5 energy-economist or sustainable-finance micro-experts; brief them on Klimathjälte angle.',
      dismissed: false,
    },
  },
  {
    id: 'con1',
    slug: 'lo-fi-authenticity-wins',
    title: 'Lo-Fi Authenticity Wins',
    description:
      'Raw, unpolished smartphone footage outperforming high-budget studio production across all metrics.',
    why_it_matters:
      'Lower production barriers. Brands can produce 10x more content for the same budget by going lo-fi.',
    category: 'content',
    origin_platform: 'tiktok',
    virality_score: 95,
    status: 'peaking',
    geo_scope: 'global',
    first_seen_at: '2026-02-15T00:00:00Z',
    last_updated: '2026-04-20T00:00:00Z',
    metadata: {
      hashtags: ['#lofi', '#raw', '#authenticcontent'],
      related_keywords: ['lo-fi content', 'unpolished', 'raw footage'],
      cover_image_url: 'https://images.unsplash.com/photo-1759393851741-674ee71fb498?w=800&q=80',
    },
    signals: [
      { id: 'sig-15', source: 'beatly_mentions', signal_type: 'keyword_velocity', raw_data: { keyword: 'lo-fi creator', velocity_ratio: 2.5 }, recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-16', source: 'tiktok_cc', signal_type: 'hashtag_spike', raw_data: { hashtag: '#lofi' }, recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-17', source: 'ad_anomaly', signal_type: 'engagement_anomaly', raw_data: { z_score: 3.1, keyword: 'lo-fi ad creative' }, recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-18', source: 'reddit', signal_type: 'viral_post', raw_data: { sample_caption: 'Lo-fi ad beat our polished studio cut by 4x on CTR' }, recorded_at: '2026-04-16T00:00:00Z' },
    ],
    snapshots: buildSnapshots(95, 21, 'peaking'),
    relevance: {
      relevance_score: 94,
      reasoning:
        'Your paid creative historically outperforms when less polished — this trend confirms the trajectory.',
      suggested_action:
        'Re-edit your top 3 brand assets into phone-shot variants; push both through Spark Ads to compare.',
      dismissed: false,
    },
  },
  {
    id: 'm1',
    slug: 'creator-whitelisting',
    title: 'Creator Whitelisting Maturity',
    description:
      'Running brand ads directly through creator accounts rather than the brand account.',
    why_it_matters:
      'Ads feel like organic recommendations. 4x better click-through rates than standard brand ads.',
    category: 'marketing',
    origin_platform: 'meta-ads',
    virality_score: 84,
    status: 'active',
    geo_scope: 'global',
    first_seen_at: '2026-01-10T00:00:00Z',
    last_updated: '2026-04-20T00:00:00Z',
    metadata: {
      hashtags: [],
      related_keywords: ['whitelisting', 'creator ads', 'spark ads'],
      cover_image_url: 'https://images.unsplash.com/photo-1759393851741-674ee71fb498?w=800&q=80',
    },
    signals: [
      { id: 'sig-19', source: 'ad_anomaly', signal_type: 'engagement_anomaly', raw_data: { keyword: 'whitelisting', z_score: 2.7 }, recorded_at: '2026-04-18T00:00:00Z' },
      { id: 'sig-20', source: 'beatly_mentions', signal_type: 'keyword_velocity', raw_data: { keyword: 'spark ads', velocity_ratio: 1.6 }, recorded_at: '2026-04-19T00:00:00Z' },
      { id: 'sig-21', source: 'newsapi', signal_type: 'viral_post', raw_data: { keyword: 'whitelisting 2026' }, recorded_at: '2026-04-15T00:00:00Z' },
    ],
    snapshots: buildSnapshots(84, 21, 'rising'),
    relevance: {
      relevance_score: 95,
      reasoning:
        'You already whitelist creators on Meta and TikTok — tapping further into this trend compounds an existing moat.',
      suggested_action:
        'Add whitelisting as a default brief clause. Pre-negotiate usage rights at contract time.',
      dismissed: false,
    },
  },
];

export const TREND_FIXTURE: Trend[] = TRENDS;
